/**
 * useCostEstimate — ask ``POST billing/estimate`` what an operation would cost before it starts.
 *
 * Only the hosted SaaS platform with billing on quotes (``useAuth().isSaas && billingEnabled``);
 * everywhere else the hook is inert and ``estimate`` stays ``null``, so the footers that render it
 * disappear. The request is debounced so a record-count input being typed does not fire a quote per
 * keystroke, and a stale response never overwrites a newer one.
 *
 * ``params`` is compared by value (JSON), so callers may build it inline. Bump ``refreshKey`` to
 * re-quote the same params (e.g. after the task's datasets changed).
 *
 * ``billable`` is true wherever a quote would be shown once there is something to price
 * (``quoted`` minus the ``params != null`` part), so a footer can hold its place with a dash
 * before the inputs are complete.
 */
import { useEffect, useMemo, useState } from 'react';

import { useAuth } from '@/features/auth/AuthContext';
import { fetchCostEstimate } from '../api/billingApi';

export const COST_ESTIMATE_DEBOUNCE_MS = 300;

export function useCostEstimate(
    operation,
    params,
    { enabled = true, debounceMs = COST_ESTIMATE_DEBOUNCE_MS, refreshKey = 0 } = {}
) {
    const { isSaas, billingEnabled } = useAuth() || {};
    const billable = isSaas === true && billingEnabled === true && enabled && Boolean(operation);
    const quoted = billable && params != null;
    const paramsKey = useMemo(() => JSON.stringify(params ?? null), [params]);
    const [state, setState] = useState({ estimate: null, loading: false, error: null });

    useEffect(() => {
        if (!quoted) {
            setState({ estimate: null, loading: false, error: null });
            return undefined;
        }
        let cancelled = false;
        setState((current) => ({ ...current, loading: true, error: null }));
        const timer = setTimeout(async () => {
            try {
                const estimate = await fetchCostEstimate(operation, JSON.parse(paramsKey));
                if (!cancelled) setState({ estimate, loading: false, error: null });
            } catch (error) {
                if (!cancelled) setState({ estimate: null, loading: false, error });
            }
        }, debounceMs);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [quoted, operation, paramsKey, debounceMs, refreshKey]);

    return { ...state, quoted, billable };
}

export default useCostEstimate;
